import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    CircularProgress,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
} from '@mui/material';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import {
    useCardCategoryQuery,
    useCardTitleListQuery,
} from 'store/slices/cards/card/card-list';
import { ItemCreatRequest, ItemListResponse } from 'types/product/product-item';
import { ItemCardProductTypeEnum } from 'types/product/product-item-type';
import pxToRem from 'assets/theme/function/px-to-rem';

const categoryOptions = [
    { label: 'Card', key: ItemCardProductTypeEnum.CARD },
    { label: 'Card Category', key: ItemCardProductTypeEnum.CARD_CATEGORY },
];

interface Props {
    control?: Control<ItemCreatRequest>;
    data?: ItemListResponse;
    errors?: FieldErrors<ItemCreatRequest>;
}

const ItemCard: React.FC<Props> = ({ control, data, errors }) => {
    const [productType, setProductType] = React.useState<
        ItemCardProductTypeEnum | ''
    >('');
    const [cardTitle, setCardTitle] = useState('');
    const { data: cardCategoryList } = useCardCategoryQuery();

    const { data: dataCard, isFetching } = useCardTitleListQuery(cardTitle);

    const handleProductTypeChange = (event: SelectChangeEvent) => {
        const value = event.target.value as ItemCardProductTypeEnum;
        setProductType(value);
    };

    const initialCardCategory = data?.itemValue?.cardCategory ?? '';
    const initialCardValue = data?.itemValue?.count ?? '';

    useEffect(() => {
        if (data && data.itemValue) {
            const cardId = data.itemValue.cardId;
            if (cardId) {
                setProductType(ItemCardProductTypeEnum.CARD);
            } else {
                setProductType(ItemCardProductTypeEnum.CARD_CATEGORY);
            }
        }
    }, [data]);

    return (
        <Box display="flex" alignItems="start" width="100%">
            <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                    value={productType}
                    label="Category"
                    onChange={handleProductTypeChange}
                >
                    {categoryOptions.map((category) => (
                        <MenuItem key={category.key} value={category.key}>
                            {category.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {productType === ItemCardProductTypeEnum.CARD && (
                <FormControl
                    error={Boolean(errors?.itemValue?.cardId)}
                    fullWidth
                    sx={{ ml: pxToRem(10) }}
                >
                    <Controller
                        name="itemValue.cardId"
                        control={control}
                        rules={{
                            required: 'Card is required',
                        }}
                        render={({ field: { onChange, value, ref } }) => (
                            <Autocomplete
                                disableClearable
                                loading={isFetching}
                                options={dataCard ?? []}
                                value={
                                    (dataCard ?? []).find(
                                        (card) => card.id === value,
                                    ) ?? undefined
                                }
                                onChange={(_, newValue) => {
                                    onChange(newValue ? newValue.id : '');
                                }}
                                inputValue={cardTitle}
                                onInputChange={(_, newInput, reason) => {
                                    if (reason !== 'reset') {
                                        setCardTitle(newInput);
                                    }
                                }}
                                getOptionLabel={(option) => option.title || ''}
                                isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        inputRef={ref}
                                        label="Card"
                                        variant="outlined"
                                        error={Boolean(
                                            errors?.itemValue?.cardId,
                                        )}
                                        helperText={errors?.itemValue?.cardId?.message?.toString()}
                                        slotProps={{
                                            input: {
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {isFetching && (
                                                            <CircularProgress
                                                                color="inherit"
                                                                size={20}
                                                            />
                                                        )}
                                                        {
                                                            params.InputProps
                                                                .endAdornment
                                                        }
                                                    </>
                                                ),
                                            },
                                        }}
                                    />
                                )}
                            />
                        )}
                    />
                </FormControl>
            )}

            {productType === ItemCardProductTypeEnum.CARD_CATEGORY && (
                <Box display="flex" alignItems="start" width="100%">
                    <FormControl
                        error={Boolean(errors?.itemValue?.cardCategory)}
                        fullWidth
                        sx={{ ml: pxToRem(10) }}
                    >
                        <InputLabel>Card Category</InputLabel>
                        <Controller
                            name="itemValue.cardCategory"
                            control={control}
                            rules={{
                                required: 'Card Category is required',
                            }}
                            render={({ field }) => (
                                <Select
                                    label="Card Category"
                                    {...field}
                                    value={
                                        field.value ??
                                        (initialCardCategory || '')
                                    }
                                >
                                    {cardCategoryList?.map((category) => (
                                        <MenuItem
                                            key={category}
                                            value={category}
                                        >
                                            {category}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                        {errors?.itemValue?.cardCategory && (
                            <FormHelperText>
                                {errors.itemValue.cardCategory.message?.toString()}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <Box sx={{ ml: pxToRem(10) }}>
                        <Controller
                            name="itemValue.count"
                            control={control}
                            rules={{
                                required: 'Card Value is required',
                                pattern: {
                                    value: /^[0-9]*[.,]?[0-9]*$/,
                                    message: 'Only numbers are allowed',
                                },
                            }}
                            render={({ field: { name, value, onChange } }) => (
                                <TextField
                                    fullWidth
                                    name={name}
                                    value={value ?? initialCardValue ?? ''}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(
                                            ',',
                                            '.',
                                        );
                                        if (/^[0-9]*\.?[0-9]*$/.test(val)) {
                                            onChange(val);
                                        }
                                    }}
                                    label="Card Value"
                                    error={Boolean(errors?.itemValue?.count)}
                                    helperText={
                                        errors?.itemValue?.count?.message?.toString() ||
                                        ''
                                    }
                                    slotProps={{
                                        input: {
                                            inputMode: 'decimal',
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default ItemCard;
